import React, { useState, useEffect, useCallback, useMemo } from "react";
import KernteamLayout from '../../KernteamLayout';
import BackArrow from '../../BackArrow';
import { host_url } from '../../App';
import ImportModal from '../ImportModal';
import LogistikConfirm from '../LogistikConfirm';
import "../logistik-base.css";
import "./Inventar.css";

const IMPORT_COLUMNS = [
  { key: 'Name',         label: 'Name',         required: true  },
  { key: 'Anzahl',       label: 'Anzahl',       required: false, parse: v => parseInt(v) || 1 },
  { key: 'Beschreibung', label: 'Beschreibung', required: false },
];

const TEMPLATE_ROWS = [
  ['Tischtennis',   2, 'Tischtennisplatten im Spielraum, Schläger erforderlich'],
  ['Badminton-Set', 3, 'Schläger und Bälle im Lagerraum'],
  ['Fußball',       5, 'Bälle beim Sportmaterial'],
  ['Frisbee',       4, ''],
];

const LEER = { Name: '', Anzahl: 1, Beschreibung: '' };

function normalisiereText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function itemText(item) {
  return normalisiereText(`${item.Name || ''} ${item.Beschreibung || ''}`);
}

async function parseResponseError(res) {
  const text = await res.text();
  try {
    const data = JSON.parse(text);
    return data.message || text;
  } catch {
    return text || `HTTP ${res.status}`;
  }
}

function Inventar() {
  const [items, setItems] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(LEER);
  const [addForm, setAddForm] = useState(LEER);
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showInaktiv, setShowInaktiv] = useState(false);
  const [suchtext, setSuchtext] = useState('');
  const [laedt, setLaedt] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState('');
  const [meldung, setMeldung] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const ladeItems = useCallback(() => {
    setLaedt(true);
    setFehler('');
    fetch(`${host_url}/logistik/inventar?alle=1`)
      .then(async r => {
        if (!r.ok) throw new Error(await parseResponseError(r));
        return r.json();
      })
      .then(data => setItems(Array.isArray(data) ? data : []))
      .catch(err => {
        console.error(err);
        setFehler(`Inventar konnten nicht geladen werden: ${err.message}`);
      })
      .finally(() => setLaedt(false));
  }, []);

  useEffect(() => { ladeItems(); }, [ladeItems]);

  const stats = useMemo(() => ({
    aktiv: items.filter(i => i.Aktiv).length,
    inaktiv: items.filter(i => !i.Aktiv).length,
    gesamt: items.length,
  }), [items]);

  const query = normalisiereText(suchtext);
  const sichtbar = useMemo(() => {
    const basis = showInaktiv ? items : items.filter(i => i.Aktiv);
    return basis
      .filter(item => !query || itemText(item).includes(query))
      .sort((a, b) => {
        if (Boolean(a.Aktiv) !== Boolean(b.Aktiv)) return a.Aktiv ? -1 : 1;
        return String(a.Name || '').localeCompare(String(b.Name || ''), 'de');
      });
  }, [items, query, showInaktiv]);

  async function sendeInventar(url, method, payload, erfolg) {
    setSpeichert(true);
    setFehler('');
    setMeldung('');
    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await parseResponseError(res));
      setMeldung(erfolg);
      await ladeItems();
      return true;
    } catch (err) {
      console.error(err);
      setFehler(err.message || 'Speichern fehlgeschlagen.');
      return false;
    } finally {
      setSpeichert(false);
    }
  }

  async function handleAdd() {
    if (!addForm.Name.trim()) return;
    const success = await sendeInventar(
      `${host_url}/logistik/inventar`,
      'POST',
      { ...addForm, Anzahl: Number(addForm.Anzahl) || 1 },
      'Gerät wurde hinzugefügt.'
    );
    if (success) {
      setAddForm(LEER);
      setShowAdd(false);
    }
  }

  async function handleEditSave(id) {
    if (!editForm.Name.trim()) return;
    const success = await sendeInventar(
      `${host_url}/logistik/inventar/${id}`,
      'PUT',
      { ...editForm, Anzahl: Number(editForm.Anzahl) || 1 },
      'Gerät wurde gespeichert.'
    );
    if (success) setEditId(null);
  }

  async function confirmDelete() {
    if (!deleteConfirm) return;
    setSpeichert(true);
    setFehler('');
    setMeldung('');
    try {
      const res = await fetch(`${host_url}/logistik/inventar/${deleteConfirm.ID}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(await parseResponseError(res));
      setMeldung('Gerät wurde gelöscht.');
      setDeleteConfirm(null);
      await ladeItems();
    } catch (err) {
      console.error(err);
      setFehler(err.message || 'Löschen fehlgeschlagen.');
    } finally {
      setSpeichert(false);
    }
  }

  async function handleToggleAktiv(item) {
    await sendeInventar(
      `${host_url}/logistik/inventar/${item.ID}`,
      'PUT',
      { ...item, Aktiv: !item.Aktiv },
      item.Aktiv ? 'Gerät wurde deaktiviert.' : 'Gerät wurde aktiviert.'
    );
  }

  function startEdit(item) {
    setShowAdd(false);
    setEditId(item.ID);
    setEditForm({ Name: item.Name, Anzahl: item.Anzahl, Beschreibung: item.Beschreibung || '', Aktiv: item.Aktiv });
  }

  return (
    <KernteamLayout>
      <div className="container inventar-container">
        <BackArrow to="/logistik" />
        <h1 className="section-title teal">Inventar</h1>

        <p className="inventar-hinweis">
          Diese Liste ist im Stormini-Portal für alle Helfer sichtbar.
        </p>

        <div className="logistik-toolbar inventar-toolbar no-print">
          <div className="logistik-search-field">
            <label htmlFor="inventar-suche">Geräte durchsuchen</label>
            <input
              id="inventar-suche"
              className="logistik-input"
              value={suchtext}
              placeholder="Name oder Hinweis"
              onChange={e => setSuchtext(e.target.value)}
            />
          </div>
          <div className="logistik-stat-strip" aria-label="Inventarübersicht">
            <span className="logistik-stat-pill">{stats.aktiv} aktiv</span>
            <span className="logistik-stat-pill">{stats.inaktiv} inaktiv</span>
            <span className="logistik-stat-pill">{stats.gesamt} gesamt</span>
          </div>
        </div>

        <div className="inventar-filter no-print">
          <label className="inaktiv-toggle">
            <input
              type="checkbox"
              checked={showInaktiv}
              onChange={e => setShowInaktiv(e.target.checked)}
            />
            Inaktive Einträge anzeigen
          </label>
        </div>

        {laedt && <div className="logistik-state-box no-print">Inventar wird geladen...</div>}
        {speichert && <div className="logistik-state-box no-print">Änderung wird gespeichert...</div>}
        {meldung && <div className="logistik-state-box no-print">{meldung}</div>}
        {fehler && <div className="logistik-state-box error no-print">{fehler}</div>}

        <div className="inventar-liste">
          {sichtbar.length === 0 && !showAdd && (
            <div className="logistik-empty">
              {items.length === 0 ? 'Keine Geräte eingetragen.' : 'Keine Geräte zur Suche gefunden.'}
            </div>
          )}

          {sichtbar.map(item => (
            <React.Fragment key={item.ID}>
              {editId === item.ID ? (
                <div className="inventar-card edit-card">
                  <InventarForm
                    form={editForm}
                    setForm={setEditForm}
                    onSave={() => handleEditSave(item.ID)}
                    onCancel={() => setEditId(null)}
                    disabled={speichert}
                  />
                </div>
              ) : (
                <div className={`inventar-card${!item.Aktiv ? ' inaktiv' : ''}`}>
                  <div className="inventar-card-main">
                    <div className="inventar-info">
                      <span className="inventar-name">{item.Name}</span>
                      <span className="inventar-anzahl">{item.Anzahl}×</span>
                      {item.Beschreibung && (
                        <span className="inventar-beschreibung">{item.Beschreibung}</span>
                      )}
                    </div>
                    <div className="inventar-card-actions no-print">
                      <button
                        className={`aktiv-toggle-btn${item.Aktiv ? ' aktiv' : ' inaktiv-btn'}`}
                        onClick={() => handleToggleAktiv(item)}
                        title={item.Aktiv ? 'Deaktivieren' : 'Aktivieren'}
                        disabled={speichert}
                      >
                        {item.Aktiv ? 'Aktiv' : 'Inaktiv'}
                      </button>
                      <button className="logistik-btn logistik-edit-btn" onClick={() => startEdit(item)} disabled={speichert}>
                        <span className="material-symbols-outlined">edit</span>
                      </button>
                      <button className="logistik-btn logistik-delete-btn" onClick={() => setDeleteConfirm(item)} disabled={speichert}>
                        <span className="material-symbols-outlined">delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}

          {showAdd && (
            <div className="inventar-card edit-card">
              <InventarForm
                form={addForm}
                setForm={setAddForm}
                onSave={handleAdd}
                onCancel={() => { setShowAdd(false); setAddForm(LEER); }}
                disabled={speichert}
              />
            </div>
          )}
        </div>

        <div className="logistik-footer no-print">
          {!showAdd && editId === null && (
            <button className="logistik-add-btn" onClick={() => setShowAdd(true)} disabled={speichert}>
              <span className="material-symbols-outlined">add</span>
              Gerät hinzufügen
            </button>
          )}
          <button className="logistik-import-btn" onClick={() => setShowImport(true)} disabled={speichert}>
            <span className="material-symbols-outlined">upload</span>
            Excel importieren
          </button>
          <button className="logistik-print-link" onClick={() => window.print()}>
            <span className="material-symbols-outlined">print</span>
            <span className="print-text">Drucken</span>
          </button>
        </div>

        {showImport && (
          <ImportModal
            title="Inventar importieren"
            columns={IMPORT_COLUMNS}
            templateName="vorlage_freizeitgeraete"
            templateRows={TEMPLATE_ROWS}
            replaceLabel="Bestehendes Inventar vorher löschen"
            onImport={async (rows, ersetzen) => {
              const res = await fetch(`${host_url}/logistik/inventar/import`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ eintraege: rows, ersetzen }),
              });
              if (!res.ok) throw new Error(await res.text());
              ladeItems();
            }}
            onClose={() => setShowImport(false)}
          />
        )}

        {deleteConfirm && (
          <LogistikConfirm
            title="Gerät löschen?"
            onCancel={() => setDeleteConfirm(null)}
            onConfirm={confirmDelete}
          >
            <p>{deleteConfirm.Name} wird dauerhaft aus der Inventar-Liste gelöscht.</p>
          </LogistikConfirm>
        )}
      </div>
    </KernteamLayout>
  );
}

function InventarForm({ form, setForm, onSave, onCancel, disabled = false }) {
  return (
    <div className="inventar-form">
      <div className="inventar-form-grid">
        <div className="inventar-field">
          <label>Name *</label>
          <input
            className="logistik-input"
            placeholder="z.B. Tischtennis"
            value={form.Name}
            disabled={disabled}
            onChange={e => setForm(f => ({ ...f, Name: e.target.value }))}
          />
        </div>
        <div className="inventar-field inventar-anzahl-field">
          <label>Anzahl</label>
          <input
            className="logistik-input"
            type="number" min="1"
            value={form.Anzahl}
            disabled={disabled}
            onChange={e => setForm(f => ({ ...f, Anzahl: parseInt(e.target.value) || 1 }))}
          />
        </div>
        <div className="inventar-field span-2">
          <label>Beschreibung / Hinweis</label>
          <input
            className="logistik-input"
            placeholder="z.B. Tischtennisplatten im Spielraum, Schläger erforderlich"
            value={form.Beschreibung}
            disabled={disabled}
            onChange={e => setForm(f => ({ ...f, Beschreibung: e.target.value }))}
          />
        </div>
      </div>
      <div className="logistik-actions inventar-form-actions">
        <button className="logistik-btn logistik-save-btn" onClick={onSave} disabled={disabled}>Speichern</button>
        <button className="logistik-btn logistik-cancel-btn" onClick={onCancel} disabled={disabled}>Abbrechen</button>
      </div>
    </div>
  );
}

export default Inventar;
